import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '@/lib/adminApi';

const RESOLUTIONS = [
  { id: 'approve', label: 'Approve' },
  { id: 'false_positive', label: 'False positive' },
  { id: 'warn', label: 'Warn' },
  { id: 'remove', label: 'Remove' },
  { id: 'suspend', label: 'Suspend' },
];

const riskClass = {
  pass: 'bg-green-50 text-green-700 border-green-100',
  flag: 'bg-orange-50 text-orange-700 border-orange-100',
  hold: 'bg-red-50 text-red-700 border-red-100',
};

const weightHelp = {
  duplicate_media: 'Higher value means reused or duplicate media increases review risk more strongly.',
  unsafe_content: 'Highest-priority signal for adult, abusive, hateful, illegal, or unsafe content.',
  profile_incomplete: 'Raises review risk when important profile fields are missing.',
  missing_contact: 'Flags profiles with weak contact, email, or identity signals.',
  missing_media: 'Flags profiles without profile image, portfolio image, video, or proof of work.',
  manual_flag: 'Risk added when admin or system manually marks a profile for review.',
};

const AdminTrustQueue = () => {
  const [status, setStatus] = useState('open');
  const [items, setItems] = useState([]);
  const [weights, setWeights] = useState(null);
  const [draftWeights, setDraftWeights] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [busyId, setBusyId] = useState('');
  const [savingWeights, setSavingWeights] = useState(false);
  const [reviewNotes, setReviewNotes] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [queueResponse, weightsResponse] = await Promise.all([
        adminApi.get('/admin/trust/queue', { params: { status } }),
        adminApi.get('/admin/trust/weights'),
      ]);
      setItems(queueResponse.data?.items || []);
      setWeights(weightsResponse.data?.weights || {});
      setDraftWeights(weightsResponse.data?.weights || {});
      setMessage('');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Trust queue could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  const saveWeights = async () => {
    setSavingWeights(true);
    try {
      const normalizedWeights = Object.entries(draftWeights).reduce((next, [key, value]) => {
        next[key] = Number(value) || 0;
        return next;
      }, {});
      const response = await adminApi.put('/admin/trust/weights', { weights: normalizedWeights });
      setWeights(response.data?.weights || normalizedWeights);
      setDraftWeights(response.data?.weights || normalizedWeights);
      setMessage('Trust scoring weights saved.');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not save trust weights.');
    } finally {
      setSavingWeights(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const resolveItem = async (item, resolution) => {
    setBusyId(`${item.id}:${resolution}`);
    try {
      await adminApi.post(`/admin/trust/queue/${item.id}/resolve`, {
        resolution,
        note: (reviewNotes[item.id] || '').trim(),
      });
      setMessage(`Trust item marked as ${resolution.replace('_', ' ')}.`);
      await load();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not resolve trust item.');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="space-y-8">
      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.35em] text-[#FF6F00]">Trust layer</p>
            <h1 className="mt-3 text-3xl font-black text-gray-950">Verification review queue</h1>
            <p className="mt-3 max-w-3xl text-gray-600">
              Silent profile checks score artist completeness and trust signals. Only flagged or held profiles appear here for admin review.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {['open', 'resolved', 'all'].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setStatus(option)}
                className={`rounded-full px-5 py-2 text-sm font-black capitalize ${
                  status === option ? 'bg-[#FF6F00] text-white' : 'border border-orange-100 bg-white text-gray-700'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </section>

      {message && <div className="rounded-2xl border border-orange-100 bg-orange-50 px-5 py-3 font-bold text-orange-800">{message}</div>}

      <section className="rounded-3xl border border-emerald-100 bg-emerald-50 p-5 shadow-sm">
        <p className="text-xs font-black uppercase tracking-[0.25em] text-emerald-700">How it works</p>
        <h2 className="mt-2 text-xl font-black text-gray-950">Trust Queue is an admin review filter, not a public score.</h2>
        <p className="mt-2 max-w-4xl text-sm font-semibold leading-6 text-gray-700">
          KalaManch silently checks profiles for missing details, duplicate media, unsafe content, weak contact signals, and manual flags.
          Only profiles that need attention appear here. Admin reviews each item, opens the profile if needed, then marks it as approve,
          false positive, warn, remove, or suspend. The numbers below control how strongly each signal contributes to review risk.
        </p>
      </section>

      <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-gray-500">Scoring weights</p>
            <h2 className="mt-2 text-2xl font-black text-gray-950">Trust Queue settings</h2>
            <p className="mt-1 text-sm font-semibold text-gray-500">Tune how strongly each profile signal contributes to review risk.</p>
          </div>
          <button type="button" className="btn-primary" onClick={saveWeights} disabled={savingWeights || !weights}>
            {savingWeights ? 'Saving...' : 'Save weights'}
          </button>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {weights &&
            Object.entries(draftWeights).map(([key, value]) => (
              <label key={key} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <span className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">{key.replaceAll('_', ' ')}</span>
                <input
                  type="number"
                  value={value}
                  onChange={(event) => setDraftWeights((current) => ({ ...current, [key]: event.target.value }))}
                  className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xl font-black text-gray-950"
                />
                <span className="mt-2 block text-xs font-semibold leading-5 text-gray-500">
                  {weightHelp[key] || 'Higher value means this signal contributes more strongly to review risk.'}
                </span>
              </label>
            ))}
        </div>
      </section>

      <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
        {loading ? (
          <div className="rounded-2xl border border-dashed border-gray-200 p-8 text-center font-bold text-gray-500">Loading trust queue...</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-green-200 bg-green-50 p-8 text-center font-bold text-green-800">
            No trust items in this view.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <article key={item.id} className="rounded-3xl border border-gray-100 bg-gray-50 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex gap-4">
                    {item.artist?.image_url && (
                      <img src={item.artist.image_url} alt="" className="h-16 w-16 rounded-2xl object-cover" />
                    )}
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-black text-gray-950">{item.artist?.name || 'Artist profile not found'}</h2>
                        <span className={`rounded-full border px-3 py-1 text-xs font-black ${riskClass[item.risk_level] || riskClass.flag}`}>
                          {item.risk_level}
                        </span>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-gray-700">Score {item.trust_score ?? '-'}</span>
                      </div>
                      <p className="mt-1 text-sm font-semibold text-gray-600">
                        {item.artist?.category || item.subject_type} {item.artist?.city ? `- ${item.artist.city}` : ''}
                      </p>
                      <p className="mt-1 text-xs font-semibold text-gray-500">
                        {[item.artist?.email, item.artist?.mobile].filter(Boolean).join(' | ') || `Artist ID: ${item.subject_id}`}
                      </p>
                    </div>
                  </div>
                  <Link className="btn-outline shrink-0 text-center" to={item.admin_profile_path || `/admin/artists?focus=${item.subject_id}`}>
                    Review artist profile
                  </Link>
                </div>
                <div className="mt-5 rounded-2xl border border-orange-100 bg-white p-4">
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-700">Generated review note</p>
                  <p className="mt-2 text-sm font-semibold leading-6 text-gray-800">
                    {item.review_note || `Review this artist profile. Trust score ${item.trust_score ?? 'not available'}; verify every issue before taking action.`}
                  </p>
                  <div className="mt-4 space-y-3">
                    {(item.issue_details?.length ? item.issue_details : (item.issues || []).map((issue) => ({ issue, recommended_check: 'Open the artist profile and verify this information before choosing an action.' }))).map((detail) => (
                      <div key={detail.issue} className="rounded-xl bg-orange-50 p-3">
                        <p className="text-sm font-black text-gray-950">Issue: {detail.issue}</p>
                        <p className="mt-1 text-sm font-semibold text-gray-600">Review: {detail.recommended_check}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <label className="mt-4 block">
                  <span className="text-sm font-black text-gray-800">Admin review note</span>
                  <textarea
                    value={reviewNotes[item.id] || ''}
                    onChange={(event) => setReviewNotes((current) => ({ ...current, [item.id]: event.target.value }))}
                    placeholder="Record what you checked, what you found, and why this action is appropriate."
                    className="mt-2 min-h-24 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-800"
                  />
                </label>
                <div className="mt-5 flex flex-wrap gap-2">
                  {RESOLUTIONS.map((resolution) => (
                    <button
                      key={resolution.id}
                      type="button"
                      disabled={Boolean(busyId)}
                      onClick={() => resolveItem(item, resolution.id)}
                      className="rounded-full border border-orange-200 bg-white px-4 py-2 text-sm font-black text-[#FF6F00] disabled:opacity-50"
                    >
                      {busyId === `${item.id}:${resolution.id}` ? 'Saving...' : resolution.label}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminTrustQueue;
